import React from 'react'
import { motion } from 'framer-motion'
import { BibleBook } from '../types/bible'
import { BookOpen, Sparkles } from 'lucide-react'

interface BookCardProps {
  book: BibleBook
  onSelectBook?: (book: BibleBook) => void
  onGenerateSlides?: (book: BibleBook, chapter?: number) => void
}

const BookCard: React.FC<BookCardProps> = ({ book, onSelectBook, onGenerateSlides }) => {
  const gradientColors = {
    old: ['#1e3a8a', '#3b82f6'],
    new: ['#dc2626', '#ef4444'],
  }

  const colors = gradientColors[book.testament]

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      whileHover={{ scale: 1.02, y: -5 }}
      transition={{ duration: 0.3 }}
      className="bg-slate-800/80 backdrop-blur-sm rounded-2xl p-6 border border-slate-700 hover:border-bible-gold/50 transition-all cursor-pointer"
      onClick={() => onSelectBook?.(book)}
    >
      {/* Book Icon */}
      <div
        className="w-20 h-20 mx-auto mb-4 rounded-xl flex items-center justify-center text-4xl shadow-lg"
        style={{
          background: `linear-gradient(135deg, ${colors[0]}, ${colors[1]})`,
        }}
      >
        {book.testament === 'old' ? '📜' : '✝️'}
      </div>

      {/* Book Info */}
      <h3 className="text-xl font-bold text-bible-gold text-center mb-2">
        {book.name}
      </h3>
      
      {book.description && (
        <p className="text-sm text-white text-center mb-4 line-clamp-2">
          {book.description}
        </p>
      )}

      <div className="flex items-center justify-center gap-2 text-xs text-white mb-4">
        <BookOpen size={14} />
        <span>{book.chapters} {book.chapters === 1 ? 'Chapter' : 'Chapters'}</span>
        <span>•</span>
        <span className="capitalize">{book.testament} Testament</span>
      </div>

      {/* Action Buttons */}
      <div className="flex gap-2">
        <button
          onClick={(e) => {
            e.stopPropagation()
            onSelectBook?.(book)
          }}
          className="flex-1 px-4 py-2 bg-bible-blue text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium"
        >
          View Chapters
        </button>
        <button
          onClick={(e) => {
            e.stopPropagation()
            onGenerateSlides?.(book)
          }}
          className="px-4 py-2 bg-bible-gold text-slate-900 rounded-lg hover:bg-yellow-500 transition-colors text-sm font-medium flex items-center gap-1"
          title="Generate AI Slides"
        >
          <Sparkles size={14} />
        </button>
      </div>
    </motion.div>
  )
}

export default BookCard

